import type { IShowroomBusinessEntityConfig } from '@makemydeal/dr-sales-checkin-types';
import type { NavRule, NavViewContext } from '../../types/showroomNavTypes';

/**
 * Business rules for every nav surface, keyed by entity `key`. Extend this map —
 * not the render pipeline — when an item needs new show/hide/disable behaviour.
 */
const rulesByKey: Record<string, NavRule> = {
    // Top nav / dropdown
    buyerInbox: { isHidden: ({ ctx }) => ctx.isBusinessBuyer },
    addCobuyer: { isHidden: ({ ctx }) => ctx.hasCobuyer },
    cobuyer: { isHidden: ({ ctx }) => !ctx.hasCobuyer },
    cobuyerInbox: { isHidden: ({ ctx }) => !ctx.hasCobuyer },
    // Share items stay visible but are disabled until their SCI rule is met (PR #10218):
    // Credit App needs a desked deal; Deal PDF needs a PDF to actually exist for the deal's
    // current version, DESKED alone doesn't mean one was ever generated.
    creditApp: { isDisabled: ({ ctx }) => !ctx.isCreditAppEnabled },
    dealPdf: { isDisabled: ({ ctx }) => !ctx.isPdfUploadEnabled },
    dealComments: { isHidden: ({ ctx }) => !ctx.isDealCommentsEnabled },
    // Left nav — can't be used until a vehicle is on the deal, unless full-page-scroll (F304636)
    // is on, in which case it stays clickable and redirects to Browse Inventory instead.
    vehicleDetails: { isDisabled: ({ ctx }) => ctx.isMissingVehicle && !ctx.isFullScrollEnabled },
    // Quick links
    manageDocuments: { isDisabled: ({ ctx }) => !ctx.hasDealRef },
    // Recent Deals is only worth surfacing when there's more than one deal.
    recentDeals: { isHidden: ({ ctx }) => ctx.recentDealsCount <= 1 }
};

/** True when the item's rule hides it. Items without a rule are shown. */
export const isNavItemHidden = (entity: IShowroomBusinessEntityConfig, ctx: NavViewContext): boolean =>
    rulesByKey[entity.key]?.isHidden?.({ entity, ctx }) ?? false;

/** True when the item's rule disables it. Items without a rule are enabled. */
export const isNavItemDisabled = (entity: IShowroomBusinessEntityConfig, ctx: NavViewContext): boolean =>
    rulesByKey[entity.key]?.isDisabled?.({ entity, ctx }) ?? false;
